import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import ConversationProductsAPI from '../../api/conversationProducts';

export const state = {
  records: {},
  totals: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getConversationProducts: _state => conversationId => {
    return _state.records[conversationId] || [];
  },
  getTotalValue: _state => conversationId => {
    // Primeiro, tenta usar o total armazenado que vem do backend
    if (_state.totals[conversationId] !== undefined) {
      return _state.totals[conversationId];
    }

    // Fallback: calcula baseado nos produtos locais
    const products = _state.records[conversationId] || [];
    return products.reduce((total, item) => {
      const itemTotal = parseFloat(item.total_value) || 0;
      return total + itemTotal;
    }, 0);
  },
};

export const actions = {
  get: async ({ commit }, conversationId) => {
    commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isFetching: true });
    try {
      const response =
        await ConversationProductsAPI.getProducts(conversationId);
      commit(types.SET_CONVERSATION_PRODUCTS, {
        conversationId,
        data: response.data.conversation_products,
      });
      commit(types.SET_CONVERSATION_PRODUCTS_TOTAL, {
        conversationId,
        totalValue: response.data.total_value,
      });
    } catch (error) {
      console.error('Error fetching conversation products:', error);
    } finally {
      commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit, dispatch }, { conversationId, productData }) => {
    console.log('🔄 Store: Criando produto de conversa:', {
      conversationId,
      productData,
    });
    commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isCreating: true });
    try {
      const response = await ConversationProductsAPI.addProduct(
        conversationId,
        productData
      );
      console.log('✅ Store: Produto criado com sucesso:', response.data);
      commit(types.ADD_CONVERSATION_PRODUCT, {
        conversationId,
        data: response.data,
      });
      // Recarregar os dados para atualizar o total
      await dispatch('get', conversationId);
      return response.data;
    } catch (error) {
      console.error('❌ Store: Erro ao criar produto:', error);
      throw error;
    } finally {
      commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isCreating: false });
    }
  },

  update: async (
    { commit, dispatch },
    { conversationId, productId, productData }
  ) => {
    commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await ConversationProductsAPI.updateProduct(
        conversationId,
        productId,
        productData
      );
      commit(types.EDIT_CONVERSATION_PRODUCT, {
        conversationId,
        data: response.data,
      });
      // Recarregar os dados para atualizar o total
      await dispatch('get', conversationId);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit, dispatch }, { conversationId, productId }) => {
    commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isDeleting: true });
    try {
      await ConversationProductsAPI.removeProduct(conversationId, productId);
      commit(types.DELETE_CONVERSATION_PRODUCT, {
        conversationId,
        productId,
      });
      // Recarregar os dados para atualizar o total
      await dispatch('get', conversationId);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CONVERSATION_PRODUCTS_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_CONVERSATION_PRODUCTS_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CONVERSATION_PRODUCTS](_state, { conversationId, data }) {
    _state.records[conversationId] = data || [];
  },

  [types.SET_CONVERSATION_PRODUCTS_TOTAL](
    _state,
    { conversationId, totalValue }
  ) {
    _state.totals[conversationId] = parseFloat(totalValue) || 0;
  },

  [types.ADD_CONVERSATION_PRODUCT](_state, { conversationId, data }) {
    if (!_state.records[conversationId]) {
      _state.records[conversationId] = [];
    }
    _state.records[conversationId].push(data);
  },

  [types.EDIT_CONVERSATION_PRODUCT](_state, { conversationId, data }) {
    const products = _state.records[conversationId] || [];
    const index = products.findIndex(item => item.id === data.id);
    if (index !== -1) {
      products[index] = data;
    }
  },

  [types.DELETE_CONVERSATION_PRODUCT](_state, { conversationId, productId }) {
    const products = _state.records[conversationId] || [];
    const index = products.findIndex(item => item.id === productId);
    if (index !== -1) {
      products.splice(index, 1);
    }
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
